"use client";

import { useEffect, useRef, useState } from "react";
import { APPLE_PAY_SDK_URL } from "@/lib/config";

interface ApplePayButtonProps {
  sdk: PayPalV6Instance;
  totalAmount: string;
  currencyCode: string;
  disabled: boolean;
  createOrder: () => Promise<string>;
  captureAndNotify: (orderId: string) => Promise<void>;
  onError?: (err: { code?: string; message: string }) => void;
  /**
   * Checkout-wide in-progress guard shared with the PayPal/card buttons.
   * When provided, an Apple Pay tap while ANY rail is mid-payment is
   * ignored (and vice versa) — closes the cross-rail double-payment gap
   * (CHECKOUT_RETRY.md §Known Limitations, parity audit 2026-07-04).
   */
  paymentInProgressRef?: { current: boolean };
}

// Singleton: load Apple Pay JS SDK once
let applePayScriptPromise: Promise<void> | null = null;

function loadApplePayScript(): Promise<void> {
  if (applePayScriptPromise) return applePayScriptPromise;

  if (typeof window !== "undefined" && window.ApplePaySession) {
    return Promise.resolve();
  }

  applePayScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = APPLE_PAY_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      applePayScriptPromise = null;
      reject(new Error("Failed to load Apple Pay SDK"));
    };
    document.head.appendChild(script);
  });

  return applePayScriptPromise;
}

export default function ApplePayButton({
  sdk,
  totalAmount,
  currencyCode,
  disabled,
  createOrder,
  captureAndNotify,
  onError,
  paymentInProgressRef,
}: ApplePayButtonProps) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for dynamic data (avoids stale closures in click handler)
  const createOrderRef = useRef(createOrder);
  createOrderRef.current = createOrder;
  const captureRef = useRef(captureAndNotify);
  captureRef.current = captureAndNotify;
  const totalAmountRef = useRef(totalAmount);
  totalAmountRef.current = totalAmount;
  const currencyCodeRef = useRef(currencyCode);
  currencyCodeRef.current = currencyCode;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Prevents concurrent payment flows (e.g., double-click, simultaneous button
  // clicks). Prefer the parent's shared checkout-wide ref so the guard also
  // covers cross-rail concurrency; local ref is the standalone fallback.
  const localPaymentInProgressRef = useRef(false);
  const isPaymentInProgressRef = paymentInProgressRef ?? localPaymentInProgressRef;

  // PayPal Apple Pay session (created once when SDK is ready)
  const paypalSessionRef = useRef<ApplePayPayPalSession | null>(null);
  const configRef = useRef<{
    merchantCapabilities: string[];
    supportedNetworks: string[];
  } | null>(null);

  const buttonRef = useRef<HTMLElement>(null);

  // Initialize: load Apple Pay script + create PayPal session + get config
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // Load Apple Pay JS SDK
        await loadApplePayScript();
        if (cancelled) return;

        // Check if Apple Pay is available on this device
        if (!window.ApplePaySession?.canMakePayments()) {
          console.log("[Apple Pay] Not available on this device/browser");
          return;
        }


        // Create PayPal Apple Pay session
        const session = await sdk.createApplePayOneTimePaymentSession();
        if (cancelled) return;
        paypalSessionRef.current = session;

        // Get Apple Pay config from PayPal
        const config = await session.config();
        if (cancelled) return;
        configRef.current = config;

        console.log("[Apple Pay] Ready:", {
          merchantCapabilities: config.merchantCapabilities,
          supportedNetworks: config.supportedNetworks,
        });

        setIsReady(true);
      } catch (err: any) {
        if (!cancelled) {
          console.error("[Apple Pay] Init failed:", err);
          setError(err.message || "Apple Pay initialization failed");
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [sdk]);

  // Attach click handler
  useEffect(() => {
    const el = buttonRef.current;
    if (!el || !isReady) return;

    const handleClick = async () => {
      if (isPaymentInProgressRef.current) {
        console.log("[Apple Pay] Payment already in progress, ignoring click");
        return;
      }
      const paypalSession = paypalSessionRef.current;
      const config = configRef.current;
      if (!paypalSession || !config) return;
      isPaymentInProgressRef.current = true;

      const paymentRequest = {
        countryCode: "US",
        currencyCode: currencyCodeRef.current,
        merchantCapabilities: config.merchantCapabilities,
        supportedNetworks: config.supportedNetworks,
        requiredBillingContactFields: ["name", "phone", "email", "postalAddress"],
        requiredShippingContactFields: [],
        total: {
          label: "Kraahl",
          amount: totalAmountRef.current,
          type: "final",
        },
      };

      // The session constructor and begin() throw SYNCHRONOUSLY (malformed
      // request, InvalidAccessError from an already-active session). With the
      // guard now shared checkout-wide, a stuck ref would brick EVERY rail,
      // not just this button — release it on any throw before the async
      // callbacks take over ownership.
      let applePaySession: InstanceType<NonNullable<typeof window.ApplePaySession>>;
      try {
        applePaySession = new window.ApplePaySession!(4, paymentRequest);
      } catch (err: any) {
        isPaymentInProgressRef.current = false;
        console.error("[Apple Pay] Session construction failed:", err);
        onErrorRef.current?.({
          code: err?.code,
          message: err?.message || "Apple Pay could not start",
        });
        return;
      }

      // Merchant validation — PayPal handles the actual validation
      applePaySession.onvalidatemerchant = (event) => {
        paypalSession
          .validateMerchant({ validationUrl: event.validationURL })
          .then((payload) => {
            applePaySession.completeMerchantValidation(payload.merchantSession);
          })
          .catch((err) => {
            console.error("[Apple Pay] Merchant validation failed:", err);
            applePaySession.abort();
          });
      };

      // Payment method selection (required callback)
      applePaySession.onpaymentmethodselected = () => {
        applePaySession.completePaymentMethodSelection({
          newTotal: paymentRequest.total,
        });
      };

      // Payment authorized — create order, confirm with PayPal, capture
      applePaySession.onpaymentauthorized = async (event) => {
        try {
          const orderId = await createOrderRef.current();

          const confirmResult = await paypalSession.confirmOrder({
            orderId,
            token: event.payment.token,
            billingContact: event.payment.billingContact,
            shippingContact: event.payment.shippingContact,
          });

          // 3DS step-up: rare for Apple Pay (biometric usually satisfies SCA),
          // but EU issuers may still mandate 3DS. Surface a clear message instead
          // of silently proceeding to capture which would fail cryptically.
          if ((confirmResult as any)?.status === "PAYER_ACTION_REQUIRED") {
            console.error("[Apple Pay] PAYER_ACTION_REQUIRED (3DS) — not supported for Apple Pay");
            applePaySession.completePayment({ status: window.ApplePaySession!.STATUS_FAILURE });
            onErrorRef.current?.({
              code: "PAYER_ACTION_REQUIRED",
              message: "Your card requires 3D Secure verification, which Apple Pay does not support here. Please use PayPal or Card.",
            });
            return;
          }

          // Capture — if this throws, money was NOT captured (confirmOrder only validates the token).
          await captureRef.current(orderId);

          applePaySession.completePayment({
            status: window.ApplePaySession!.STATUS_SUCCESS,
          });
        } catch (err: any) {
          console.error("[Apple Pay] Payment failed:", err);
          applePaySession.completePayment({
            status: window.ApplePaySession!.STATUS_FAILURE,
          });
          // Surface to the parent error banner so the buyer sees what happened
          onErrorRef.current?.({
            code: err?.code,
            message: err?.message || "Apple Pay payment failed",
          });
        } finally {
          isPaymentInProgressRef.current = false;
        }
      };

      applePaySession.oncancel = () => {
        console.log("[Apple Pay] Payment cancelled by user");
        isPaymentInProgressRef.current = false;
      };

      try {
        applePaySession.begin();
      } catch (err: any) {
        isPaymentInProgressRef.current = false;
        console.error("[Apple Pay] begin() failed:", err);
        onErrorRef.current?.({
          code: err?.code,
          message: err?.message || "Apple Pay could not start",
        });
      }
    };

    console.log("[Apple Pay] Attaching click handler");
    el.addEventListener("click", handleClick);
    return () => el.removeEventListener("click", handleClick);
  }, [isReady]);

  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400 text-center py-1">
        Apple Pay unavailable
      </div>
    );
  }

  if (!isReady) return null;

  return (
    <div
      className="mt-2"
      style={{
        ...(disabled ? { pointerEvents: "none", opacity: 0.5 } : {}),
      }}
    >
      {/* @ts-expect-error — apple-pay-button is a web component from Apple Pay JS SDK */}
      <apple-pay-button
        ref={buttonRef}
        buttonstyle="black"
        type="buy"
        locale="en"
        style={{
          display: "block",
          width: "100%",
          height: 55,
          borderRadius: 30,
          WebkitAppearance: "-apple-pay-button" as any,
        }}
      />
    </div>
  );
}
